/**
 * coordinators.service.ts
 *
 * Thin wrapper over GET /v5/coordinators, scoped by the X-Target-Company
 * header injected by apiClient. Used by the New Lead form's Coordinator
 * select and any future feature that needs the account's coordinator list.
 *
 * The server returns snake_case fields (first_name / last_name) via
 * CoordinatorsController::index; we re-map to camelCase for the V5 UI.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface Coordinator {
  coordID: number;
  firstName: string;
  lastName: string;
  suffix?: string;
  title?: string;
  email?: string;
  active: boolean;
}

interface CoordinatorApiRow {
  coordID: number;
  first_name?: string;
  last_name?: string;
  suffix?: string;
  title?: string;
  email?: string;
  active?: boolean;
}

export interface ListCoordinatorsFilters {
  /** Pass `false` for ONLY inactive coordinators. Omit (or `true`) for active-only. */
  active?: boolean;
  /**
   * Coordinator ids the caller's record already holds. They come back whatever
   * their state — flagged `active: false` — so an assigned-but-departed ECC still
   * renders in the field that stores them. See lib/staffOptions.
   */
  includeIds?: number[];
}

export function listCoordinators(filters: ListCoordinatorsFilters = {}): Promise<Coordinator[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.active !== undefined) params.active = filters.active;
  if (filters.includeIds?.length) params.includeIds = filters.includeIds.join(',');

  return apiClient
    .get<ApiResponse<CoordinatorApiRow[]>>('/coordinators', { params })
    .then((r) =>
      (r.data.data ?? []).map((c) => ({
        coordID: c.coordID,
        firstName: c.first_name ?? '',
        lastName: c.last_name ?? '',
        suffix: c.suffix ?? '',
        title: c.title ?? '',
        email: c.email ?? '',
        active: !!c.active,
      })),
    );
}

export function formatCoordinatorName(c: Pick<Coordinator, 'firstName' | 'lastName'>): string {
  return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
}
